/**
 * Document Type Utilities
 *
 * Helper functions for checking document source types across the application.
 * Provides a single source of truth for document type logic.
 */

/**
 * Check if a document is a user-uploaded document (custom or manual).
 * These documents can be viewed directly via the document viewer.
 *
 * @param sourceType - The source_type field from the document/source
 * @returns true if the document is custom or manual type
 */
export const isUserUploadedDocument = (sourceType: string): boolean => {
  return ['custom', 'manual'].includes(sourceType);
};

/**
 * Check if a document is viewable in the UI.
 * Includes custom, manual, and FAQ documents that have view actions.
 *
 * @param sourceType - The source_type field from the document/source
 * @returns true if the document can be viewed in the application
 */
export const isViewableDocument = (sourceType: string): boolean => {
  return ['custom', 'manual', 'faq'].includes(sourceType);
};

/**
 * Check if a document is an FAQ document.
 * FAQs open in a dialog rather than a separate viewer.
 *
 * @param sourceType - The source_type field from the document/source
 * @returns true if the document is an FAQ
 */
export const isFaqDocument = (sourceType: string): boolean => {
  return sourceType === 'faq';
};

/**
 * Check if a document is an external SAP document.
 * These documents open in a new tab to their external URL.
 *
 * @param sourceType - The source_type field from the document/source
 * @returns true if the document is an SAP external document
 */
export const isSapDocument = (sourceType: string): boolean => {
  return sourceType === 'sap';
};
